<script setup>
import { computed, ref, watch } from 'vue';
import CFUnderlinedButton from '@/shared/components/CFUnderlinedButton.vue';
import { usePopper } from '@/shared/composables/usePopper.js';

defineProps({
    text: {
        type: String,
        required: true,
    },
    icon: {
        /** @type import('vue').PropType<IconName> */
        type: String,
        default: undefined,
    },
    dropdownClass: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['closed']);

const show = ref(false);

const [buttonRef, dropdownRef] = usePopper({
    placement: 'bottom-end',
    offsetDistance: 0,
});

const close = () => {
    show.value = false;
};

watch(show, (newValue) => {
    if (!newValue) {
        emit('closed');
    }
});

const buttonStyles = computed(() => {
    if (!buttonRef.value?.$el) {
        return {};
    }

    const rect = buttonRef.value.$el.getBoundingClientRect();
    return {
        top: `${rect.top + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
    };
});

defineExpose({ show, close });
</script>

<template>
    <div class="relative inline-block">
        <Teleport to="body">
            <Transition name="fade">
                <div
                    v-if="show"
                    class="fixed inset-0 z-50 h-screen w-screen bg-black/35"
                    @click="close"
                />
            </Transition>
        </Teleport>

        <div class="relative flex justify-end">
            <CFUnderlinedButton
                ref="buttonRef"
                :text="text"
                :icon="icon"
                :aria-expanded="show"
                @click="show = !show"
            />

            <!-- Teleport duplicate button to maintain visual continuity during dropdown animation -->
            <Teleport to="body">
                <CFUnderlinedButton
                    v-if="show"
                    :text="text"
                    :icon="icon"
                    class="pointer-events-none fixed z-60 bg-neutral-100"
                    :style="buttonStyles"
                />
            </Teleport>

            <Teleport to="body">
                <div
                    v-if="show"
                    ref="dropdownRef"
                    class="z-60"
                >
                    <Transition
                        appear
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <div
                            class="dropdown-options min-w-80 bg-neutral-100 p-5 shadow-lg"
                            :class="dropdownClass"
                        >
                            <slot :close="close" />
                        </div>
                    </Transition>
                </div>
            </Teleport>
        </div>
    </div>
</template>
